import {
  ApiAction,
  ApiContext,
  ApiItemType,
  ApiReturnFrontendPermission,
} from '@highimpact-frontend/api-types';
import { Typography } from '@mui/material';
import { forwardRef, useEffect, useState } from 'react';
import { usePermissionFetch } from './permission-context';

type PermissionDecoratorV2Props = {
  item: ApiItemType;
  project_id?: number;
  site_id?: number;
  company_id?: number;
  children?: React.ReactNode;
  style?: React.CSSProperties;
};

const PermissionDecoratorV2 = forwardRef<
  HTMLDivElement,
  PermissionDecoratorV2Props
>(
  (
    { children, item, project_id, site_id, company_id, style, ...props },
    ref
  ) => {
    const fetchPermission = usePermissionFetch();
    const params: ApiContext = {
      project_id: project_id ? Number(project_id) : null,
      site_id: site_id ? Number(site_id) : null,
      company_id: company_id ? Number(company_id) : null,
      item,
    };

    const [data, setData] = useState<ApiReturnFrontendPermission | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      if (!fetchPermission) {
        setIsLoading(false);
        return undefined;
      }

      let isMounted = true;
      const controller = new AbortController();
      setIsLoading(true);

      fetchPermission(params, controller.signal)
        .then((res) => {
          if (isMounted) {
            setData(res);
            setError(null);
          }
        })
        .catch((err) => {
          const isCanceled = (err as Error)?.name === 'CanceledError';
          if (isMounted && !isCanceled) {
            setError(err);
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false);
          }
        });

      return () => {
        isMounted = false;
        controller.abort();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company_id, fetchPermission, item, project_id, site_id]);

    if (!fetchPermission || error || isLoading || !data) {
      return null;
    }

    const action: ApiAction = data['action'] ?? 'hide';

    if (action === 'hide') {
      return null;
    }

    if (action === 'hide_with_message') {
      return (
        <Typography>
          Vous n&apos;avez pas les permissions nécessaires pour accéder à cette
          page
        </Typography>
      );
    }

    if (action === 'disable') {
      return (
        <div ref={ref} style={{ opacity: 0.5, pointerEvents: 'none' }}>
          {children}
        </div>
      );
    }

    return (
      <div ref={ref} {...props} style={style}>
        {children}
      </div>
    );
  }
);

PermissionDecoratorV2.displayName = 'PermissionDecoratorV2';

export default PermissionDecoratorV2;
